import { Component } from '@angular/core';
import { Router } from '@angular/router';
import { IonApp, IonRouterOutlet } from '@ionic/angular/standalone';

import { App, URLOpenListenerEvent } from '@capacitor/app';
import { Browser } from '@capacitor/browser';
import { Capacitor } from '@capacitor/core';

import { MsalService } from '@azure/msal-angular';
import { CustomerRealTimeNotificationsService } from 'app/core/signalr/customer-real-time-notifications.service';
import { clearMsalInteractionState, NATIVE_LOGOUT_PENDING_KEY } from 'app/core/auth/native-logout-state';
import { MobileUpdateService } from 'app/core/updates/mobile-update.service';

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [IonApp, IonRouterOutlet],
  template: '<ion-app><ion-router-outlet></ion-router-outlet></ion-app>'
})
export class AppComponent {
  handleAuthRedirectUrl: (url: string) => void = () => undefined;

  constructor(
    private readonly msal: MsalService,
    private readonly router: Router,
    private readonly realtime: CustomerRealTimeNotificationsService,
    private readonly mobileUpdates: MobileUpdateService,
  ) {
    const platform = typeof Capacitor?.getPlatform === 'function' ? Capacitor.getPlatform() : 'web';
    void this.mobileUpdates.checkForUpdates();

    const handleAuthRedirectUrl = (url: string) => {
      if (!url) return;

      const lower = url.toLowerCase();

      // iOS uses the Lumivo bundle identifier. Google Play retains the
      // original Android package id so existing installs can be upgraded.
      const isNativeAuthRedirect = [
        'com.lumivo.postalbridge://auth-redirect',
        'com.postalbridge.customer://auth-redirect',
      ].some(prefix => lower.startsWith(prefix));
      if (!isNativeAuthRedirect) {
        return;
      }

      // The native MSAL navigation client opens B2C in Capacitor Browser.
      // Receiving the deep link brings the WebView forward, but iOS can leave
      // the browser sheet covering it until it is explicitly dismissed.
      this.closeNativeAuthBrowser(platform);

      if (sessionStorage.getItem(NATIVE_LOGOUT_PENDING_KEY)) {
        clearMsalInteractionState();
        sessionStorage.removeItem(NATIVE_LOGOUT_PENDING_KEY);
        sessionStorage.removeItem('pb.postLoginUrl');
        try { this.msal.instance.setActiveAccount(null); } catch { /* ignore */ }
        void this.router.navigateByUrl('/login', { replaceUrl: true });
        return;
      }

      // MSAL can parse either fragment-style (#...) or query-style (?code=...).
      // B2C commonly returns the auth code in the query string.
      const queryIndex = url.indexOf('?');
      const hashIndex = url.indexOf('#');
      const responsePart =
        queryIndex >= 0
          ? url.substring(queryIndex) // includes leading '?'
          : hashIndex >= 0
            ? url.substring(hashIndex) // includes leading '#'
            : '';

      this.msal.instance
        .handleRedirectPromise(responsePart || undefined)
        .then((result) => {
          if (result?.account) {
            this.msal.instance.setActiveAccount(result.account);
          }

          const accounts = this.msal.instance.getAllAccounts();
          if (!this.msal.instance.getActiveAccount() && accounts.length > 0) {
            this.msal.instance.setActiveAccount(accounts[0]);
          }

          const dest = sessionStorage.getItem('pb.postLoginUrl') || '/tabs/inbox';
          sessionStorage.removeItem('pb.postLoginUrl');

          // If we got a result OR we now have accounts, treat this as signed in.
          if (result || accounts.length) {
            void this.realtime.ensureJoined();
            void this.router.navigateByUrl(dest, { replaceUrl: true });
            this.closeNativeAuthBrowser(platform);
            return;
          }

          // Post-logout deep link commonly returns to the app without an auth result
          // and with no cached accounts. Treat that as signed-out state.
          void this.router.navigateByUrl('/login', { replaceUrl: true });
        })
        .catch(() => {
          // Avoid noisy logs in production; auth-redirect page will handle fallback.
        });
    };

    this.handleAuthRedirectUrl = handleAuthRedirectUrl;

    // Handle native deep links from system (Azure B2C redirect back into the app).
    App.addListener('appUrlOpen', (event: URLOpenListenerEvent) => {
      this.handleAuthRedirectUrl(event?.url ?? '');
    });

    // Cold-start support: if the app is launched from the auth redirect URL,
    // appUrlOpen may not fire, so read the launch URL once.
    App.getLaunchUrl()
      .then((launch) => this.handleAuthRedirectUrl(launch?.url ?? ''))
      .catch(() => {
        /* ignore */
      });

    // Optional: useful when debugging device behavior.
    // eslint-disable-next-line no-console
    if (typeof Capacitor?.getPlatform === 'function') console.log('[Customer] platform', platform);

    // Mirror Mail Center behavior: always let MSAL process a pending redirect
    // as early as possible on web, so `interaction_in_progress` doesn't get stuck.
    if (platform === 'web') {
      this.msal.instance
        .handleRedirectPromise()
        .then((result) => {
          if (result?.account) this.msal.instance.setActiveAccount(result.account);

          const accounts = this.msal.instance.getAllAccounts();
          if (!this.msal.instance.getActiveAccount() && accounts.length > 0) {
            this.msal.instance.setActiveAccount(accounts[0]);
          }

          const dest = sessionStorage.getItem('pb.postLoginUrl');
          if (dest && (result || accounts.length)) {
            sessionStorage.removeItem('pb.postLoginUrl');
            void this.realtime.ensureJoined();
            void this.router.navigateByUrl(dest, { replaceUrl: true });
          }
        })
        .catch(() => {
          /* ignore */
        });
    }

    // Also attempt to start realtime on warm loads (already signed in).
    if (this.msal.instance.getActiveAccount() || this.msal.instance.getAllAccounts().length) {
      void this.realtime.ensureJoined();
    }
  }

  closeNativeAuthBrowser(platform: string): void {
    if (platform !== 'ios' && platform !== 'android') return;

    void Browser.close().catch(() => {
      /* browser may already be closed */
    });
  }
}
